/**
 * A tolerant XML tree, with namespaces resolved (M1.2.3, M1.2.7).
 *
 * ## Why tolerant rather than strict
 *
 * M1.2.7 requires that "recoverable errors yield a partial feed plus warnings
 * rather than failing". A strict parser cannot do that: it throws on the first
 * well-formedness error and hands back nothing, so a feed truncated after item
 * one yields zero items instead of one. The corpus has that exact case in
 * `malformed/unclosed-tags`, with a complete first item before the truncation
 * specifically so "recovered what it could" is distinguishable from "gave up".
 *
 * So the tokenizer is `htmlparser2`, which is built for markup the web
 * actually serves and does not throw. Well-formedness problems are then
 * detected structurally and reported as warnings — the document is read *and*
 * the operator is told it was broken, rather than one or the other.
 *
 * ## Namespaces are resolved to URIs, not matched by prefix
 *
 * `dc:date` is conventional, not required: a feed may bind Dublin Core to any
 * prefix it likes, or bind `dc:` to something else entirely. Matching on the
 * resolved URI is barely more code than matching the prefix and is the only
 * version that is actually correct. It also makes Atom-inside-RSS work, which
 * real feeds do for `atom:link rel="self"`.
 */

import { decodeHTML, decodeXML } from 'entities';
import { Parser } from 'htmlparser2';

import type { ParseWarning } from './types.js';

/** Namespace URIs feeds actually use. */
export const NS = {
  atom: 'http://www.w3.org/2005/atom',
  rss1: 'http://purl.org/rss/1.0/',
  rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  dc: 'http://purl.org/dc/elements/1.1/',
  content: 'http://purl.org/rss/1.0/modules/content/',
  itunes: 'http://www.itunes.com/dtds/podcast-1.0.dtd',
  media: 'http://search.yahoo.com/mrss/',
  xhtml: 'http://www.w3.org/1999/xhtml',
  /** RSS 2.0 elements are in no namespace at all. */
  none: '',
} as const;

export interface XmlElement {
  /** Local name, lowercased. */
  readonly name: string;
  /** Resolved namespace URI, lowercased; `''` for no namespace. */
  readonly ns: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly XmlNode[];
}

export interface XmlText {
  readonly text: string;
  /** CDATA is tracked because its content is never entity-decoded. */
  readonly cdata: boolean;
}

export type XmlNode = XmlElement | XmlText;

export const isElement = (node: XmlNode): node is XmlElement => 'name' in node;

export interface XmlDocument {
  readonly root: XmlElement | undefined;
  readonly warnings: readonly ParseWarning[];
}

/* ----------------------------------------------------------------- parsing -- */

interface Frame {
  readonly element: MutableElement;
  /** Prefix-to-URI bindings introduced by this element. */
  readonly bindings: Map<string, string>;
  /** Written as `<x/>`, so the close htmlparser2 reports is not a repair. */
  readonly selfClosing: boolean;
}

interface MutableElement {
  name: string;
  ns: string;
  attrs: Record<string, string>;
  children: XmlNode[];
}

/**
 * Named entities XML defines. Everything else is an error in XML and
 * commonplace in feeds.
 */
const XML_ENTITIES = new Set(['amp', 'lt', 'gt', 'quot', 'apos']);

const NAMED_ENTITY = /&([A-Za-z][A-Za-z0-9]{1,31});/g;

export function parseXml(text: string): XmlDocument {
  const warnings: ParseWarning[] = [];
  const stack: Frame[] = [];
  let root: MutableElement | undefined;
  let sawUndefinedEntity = false;
  /*
   * CDATA content is literal. `<![CDATA[&amp;]]>` means the five characters
   * `&amp;`, not an ampersand, so decoding it would corrupt every feed that
   * wraps escaped markup in CDATA — which `markup/cdata-bodies` does.
   */
  let inCdata = false;
  /*
   * Elements htmlparser2 had to close for us. That is the signal for a
   * truncated document, and it cannot be read off the stack: `parser.end()`
   * synthesises a close for everything still open, so the stack is always
   * empty by the time parsing finishes.
   */
  let impliedCloses = 0;

  /**
   * Text is decoded with the **HTML** entity table, not the XML one.
   *
   * `&nbsp;` and `&mdash;` are undefined in XML and make a document not
   * well-formed — and they appear in real feeds constantly, which is what
   * `malformed/undefined-entity` records. Refusing them would discard whole
   * feeds over one character, so they are decoded and warned about instead.
   */
  const decodeText = (value: string): string => {
    NAMED_ENTITY.lastIndex = 0;
    for (
      let match = NAMED_ENTITY.exec(value);
      match !== null;
      match = NAMED_ENTITY.exec(value)
    ) {
      if (!XML_ENTITIES.has(match[1] ?? '')) {
        sawUndefinedEntity = true;
        break;
      }
    }
    return decodeHTML(value);
  };

  const resolve = (prefix: string): string => {
    for (let index = stack.length - 1; index >= 0; index -= 1) {
      const bound = stack[index]?.bindings.get(prefix);
      if (bound !== undefined) {
        return bound;
      }
    }
    return '';
  };

  /*
   * Declared before the handlers so they can read `parser.endIndex`, which is
   * how a self-closing tag is told apart from a truncated one. htmlparser2
   * reports `isImplied: true` for both — `<atom:link href="x"/>` and an
   * element the document never closed — so relying on that flag alone would
   * warn about almost every real feed.
   */
  const parser: Parser = new Parser(
    {
      onopentag(rawName, rawAttrs) {
        const bindings = new Map<string, string>();
        const attrs: Record<string, string> = {};

        for (const [key, value] of Object.entries(rawAttrs)) {
          const lower = key.toLowerCase();

          if (lower === 'xmlns') {
            bindings.set('', value.trim().toLowerCase());
          } else if (lower.startsWith('xmlns:')) {
            bindings.set(lower.slice(6), value.trim().toLowerCase());
          } else {
            // Attribute values carry entities too — `href="?a=1&amp;b=2"`.
            attrs[lower] = decodeXML(value);
          }
        }

        const [prefix, local] = splitName(rawName.toLowerCase());
        const element: MutableElement = { name: local, ns: '', attrs, children: [] };

        // `<x/>` ends `/>`; `<x>` ends `>`. endIndex points at the `>`.
        const selfClosing = text[parser.endIndex - 1] === '/';

        const frame: Frame = { element, bindings, selfClosing };
        stack.push(frame);

        // Resolved after pushing, so an element declaring its own default
        // namespace is in it — which is how every Atom feed is written.
        element.ns = resolve(prefix);

        const parent = stack[stack.length - 2]?.element;
        if (parent === undefined) {
          root ??= element;
        } else {
          parent.children.push(element);
        }
      },

      ontext(value) {
        const frame = stack[stack.length - 1];
        if (frame === undefined || value === '') {
          return;
        }
        frame.element.children.push(
          inCdata
            ? { text: value, cdata: true }
            : { text: decodeText(value), cdata: false },
        );
      },

      oncdatastart() {
        inCdata = true;
      },

      oncdataend() {
        inCdata = false;
      },

      onclosetag(rawName, isImplied) {
        const [, local] = splitName(rawName.toLowerCase());
        const top = stack[stack.length - 1];

        if (top !== undefined && top.element.name !== local) {
          // A mismatched name means the document was not well-formed.
          // Recorded rather than rejected.
          warnings.push({
            code: 'not-well-formed',
            detail: `</${local}> closed <${top.element.name}>`,
          });
        }

        if (isImplied && top?.selfClosing !== true) {
          impliedCloses += 1;
        }

        stack.pop();
      },
    },
    {
      xmlMode: true,
      // Handled above with the HTML table, so the tokenizer must not also
      // decode and leave a doubly-decoded `&amp;amp;` as a bare `&`.
      decodeEntities: false,
      recognizeCDATA: true,
      recognizeSelfClosing: true,
      lowerCaseTags: false,
      lowerCaseAttributeNames: false,
    },
  );

  parser.write(text);
  parser.end();

  if (impliedCloses > 0) {
    warnings.push({
      code: 'unclosed-elements',
      detail: `${String(impliedCloses)} element(s) were closed for us; the document is truncated or malformed`,
    });
  }

  if (sawUndefinedEntity) {
    warnings.push({
      code: 'undefined-entity',
      detail: 'HTML named entities were used, which XML does not define; decoded anyway',
    });
  }

  return { root: root as XmlElement | undefined, warnings };
}

const splitName = (name: string): [string, string] => {
  const colon = name.indexOf(':');
  return colon === -1 ? ['', name] : [name.slice(0, colon), name.slice(colon + 1)];
};

/* --------------------------------------------------------------- accessors -- */

/** Direct children matching a local name, optionally in a namespace. */
export function elements(
  parent: XmlElement | undefined,
  name: string,
  ns?: string,
): readonly XmlElement[] {
  if (parent === undefined) {
    return [];
  }
  return parent.children.filter(
    (node): node is XmlElement =>
      isElement(node) && node.name === name && (ns === undefined || node.ns === ns),
  );
}

export const element = (
  parent: XmlElement | undefined,
  name: string,
  ns?: string,
): XmlElement | undefined => elements(parent, name, ns)[0];

/**
 * The first matching child from several candidates, in order of preference.
 *
 * How format variation is handled without a cascade of `??`: an RSS item's
 * date may be `pubDate`, `dc:date` or `atom:updated`, and the order expresses
 * which the feed most likely meant.
 */
export function firstOf(
  parent: XmlElement | undefined,
  candidates: readonly (readonly [string, string | undefined])[],
): XmlElement | undefined {
  for (const [name, ns] of candidates) {
    const found = element(parent, name, ns);
    if (found !== undefined) {
      return found;
    }
  }
  return undefined;
}

/** All descendant text, concatenated and trimmed. */
export function textOf(node: XmlElement | undefined): string {
  if (node === undefined) {
    return '';
  }
  let out = '';
  const walk = (current: XmlElement): void => {
    for (const child of current.children) {
      if (isElement(child)) {
        walk(child);
      } else {
        out += child.text;
      }
    }
  };
  walk(node);
  return out.trim();
}

/** Text, or `undefined` when the element is absent or empty. */
export const textOrUndefined = (node: XmlElement | undefined): string | undefined => {
  const value = textOf(node);
  return value === '' ? undefined : value;
};

export const attr = (node: XmlElement | undefined, name: string): string | undefined => {
  const value = node?.attrs[name];
  return value === undefined || value === '' ? undefined : value;
};

/**
 * An element's children re-serialised as markup.
 *
 * Needed for Atom's `content type="xhtml"`, where the body is child *elements*
 * rather than escaped text — so `textOf` would return the prose with every tag
 * silently removed.
 */
export function innerMarkup(node: XmlElement | undefined): string {
  if (node === undefined) {
    return '';
  }

  let out = '';
  for (const child of node.children) {
    out += isElement(child) ? serialise(child) : escapeText(child.text);
  }
  return out.trim();
}

function serialise(node: XmlElement): string {
  const attrs = Object.entries(node.attrs)
    .map(([key, value]) => ` ${key}="${escapeAttr(value)}"`)
    .join('');

  const inner = node.children
    .map((child) => (isElement(child) ? serialise(child) : escapeText(child.text)))
    .join('');

  return inner === ''
    ? `<${node.name}${attrs} />`
    : `<${node.name}${attrs}>${inner}</${node.name}>`;
}

const escapeText = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeAttr = (value: string): string => escapeText(value).replace(/"/g, '&quot;');

/**
 * The nearest `xml:base`, walking up from an element.
 *
 * Atom uses it to shorten every link in a feed to a path, so ignoring it turns
 * every entry link into a relative URL with no base — which is why
 * `formats/atom1` carries one.
 */
export function baseOf(chain: readonly XmlElement[]): string | undefined {
  for (let index = chain.length - 1; index >= 0; index -= 1) {
    const base = attr(chain[index], 'xml:base');
    if (base !== undefined) {
      return base;
    }
  }
  return undefined;
}
